/**
 * Progressive enhancement for the collection results.
 *
 * Every filter is a link or a GET form and "Toon meer" is a link to the next page, so the page
 * works without JavaScript. With JavaScript the filters, sorting and pagination fetch the new page
 * and swap only the filter column and the results, so the page itself never reloads.
 */

/**
 * The section-only version of a results URL (Section Rendering API): the sold-out split fetches
 * only this section, which renders faster than the whole page.
 */
export const sectionUrl = (href, sectionId, base = window.location.href) => {
  const url = new URL(href, base);
  url.searchParams.set('section_id', sectionId);
  return `${url.pathname}${url.search}`;
};

/** The page URL behind a link from a section-only response, so a new tab gets the whole page. */
export const pageUrl = (href, base = window.location.href) => {
  const url = new URL(href, base);
  url.searchParams.delete('section_id');
  return `${url.pathname}${url.search}`;
};

/** The same results from their first page: the sold-out split always starts at the top. */
export const firstPageUrl = (href, base = window.location.href) => {
  const url = new URL(href, base);
  url.searchParams.delete('page');
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * The URL a GET form of the results submits to. The predictive search's prefix option has no Liquid
 * counterpart to render as a field, so it comes along from the current URL: sorting keeps the search.
 */
export const formUrl = (action, data, current = window.location.href) => {
  const url = new URL(action, current);
  url.search = new URLSearchParams(data).toString();
  const prefix = new URL(current).searchParams.get('options[prefix]');
  if (prefix !== null && !url.searchParams.has('options[prefix]')) url.searchParams.set('options[prefix]', prefix);
  return `${url.pathname}${url.search}`;
};

/** Whether a results URL opens on a later page than the first. */
export const isLaterPage = (href, base = window.location.href) =>
  Number(new URL(href, base).searchParams.get('page')) > 1;

const layout = document.querySelector('.collection-grid__layout');

if (layout) {
  const filters = () => layout.querySelector('.collection-grid__filters');
  const results = () => layout.querySelector('.collection-grid__results');
  const toggle = () => layout.querySelector('.collection-grid__toggle');
  const backdrop = () => layout.querySelector('.collection-grid__backdrop');

  const setDrawer = (open) => {
    const panel = filters();
    if (!panel) return;

    panel.toggleAttribute('data-open', open);
    toggle()?.setAttribute('aria-expanded', String(open));
    document.documentElement.toggleAttribute('scroll-lock', open);

    const shade = backdrop();
    if (shade) shade.hidden = !open;

    if (open) layout.querySelector('.collection-grid__close')?.focus();
    else toggle()?.focus();
  };

  const paintPrice = () => {
    const form = layout.querySelector('.collection-grid__price');
    if (!form) return;

    const slider = form.querySelector('.collection-grid__slider');
    const minInput = form.querySelector('[data-role="min"]');
    const maxInput = form.querySelector('[data-role="max"]');
    const ceiling = Number(maxInput.max);

    if (Number(minInput.value) > Number(maxInput.value)) {
      if (document.activeElement === minInput) minInput.value = maxInput.value;
      else maxInput.value = minInput.value;
    }

    slider.style.setProperty('--from', `${(Number(minInput.value) / ceiling) * 100}%`);
    slider.style.setProperty('--to', `${(Number(maxInput.value) / ceiling) * 100}%`);
    form.querySelector('[data-price-from]').textContent = minInput.value;
    form.querySelector('[data-price-to]').textContent = maxInput.value;
  };

  const swap = async (url, { append = false, push = true, focusHref = null, insideDrawer = false } = {}) => {
    layout.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status}`);

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');

      if (append) {
        // Codex fix 2026-09-22, item 1: a fetched page mixes products with pages/articles
        // (search only), so both result containers get their new items appended, not just
        // the product grid, or later pages of non-product hits silently disappeared.
        const productList = results().querySelector('[data-results-products]');
        const nextProductList = markup.querySelector('[data-results-products]');
        if (productList && nextProductList) {
          markup.querySelectorAll('[data-results-products] > li').forEach((item) => productList.append(item));
        }

        const otherList = results().querySelector('[data-results-other-list]');
        const nextOther = markup.querySelector('[data-results-other]');
        if (otherList) {
          markup.querySelectorAll('[data-results-other-list] > li').forEach((item) => otherList.append(item));
        } else if (nextOther) {
          // The current page never rendered the other-results block (e.g. an older cached
          // page); insert the fetched block whole instead of appending into nothing.
          const anchor = results().querySelector('[data-results-products]');
          anchor.insertAdjacentElement('afterend', nextOther);
        }

        const pagination = results().querySelector('.collection-grid__pagination');
        const nextPagination = markup.querySelector('.collection-grid__pagination');
        if (nextPagination) pagination.replaceWith(nextPagination);
        else pagination.remove();
      } else {
        const nextResults = markup.querySelector('.collection-grid__results');
        if (!nextResults) {
          // The fetched document has no results container to swap in, e.g. a
          // menu link that isn't a real collection destination slipped
          // through, or an unexpected response body. Fall back to a normal
          // navigation instead of replacing the grid with nothing (Codex
          // review finding).
          window.location.assign(url);
          return;
        }

        const wasOpen = filters()?.hasAttribute('data-open');
        // Selecting a chip inside the open mobile drawer should close it and
        // return to the results, instead of leaving it hovering over the
        // freshly swapped grid (Codex review finding).
        const closeDrawerAfter = insideDrawer && wasOpen;
        const keepDrawerOpen = wasOpen && !closeDrawerAfter;
        // A zero-result search drops the filter column entirely (item 6): the
        // fetched page, the current page, or both, may have no filters
        // element to swap into (Codex review finding).
        const nextFilters = markup.querySelector('.collection-grid__filters');

        if (wasOpen && (!nextFilters || closeDrawerAfter)) {
          // Close it first so its scroll-lock and backdrop clear normally,
          // instead of removing the panel out from under an open drawer and
          // trapping the page behind a backdrop nothing can close anymore
          // (Codex review finding).
          setDrawer(false);
        }

        results().replaceWith(nextResults);

        if (nextFilters) {
          if (filters()) {
            filters().innerHTML = nextFilters.innerHTML;
            if (keepDrawerOpen) filters().setAttribute('data-open', '');
          } else {
            layout.insertBefore(nextFilters, results());
            if (keepDrawerOpen) nextFilters.setAttribute('data-open', '');
          }
        } else {
          filters()?.remove();
        }
        layout.classList.toggle('collection-grid__layout--no-filters', !nextFilters);

        paintPrice();
        startSplit(url);

        const header = document.querySelector('.collection-header__inner');
        const nextHeader = markup.querySelector('.collection-header__inner');
        if (header && nextHeader) {
          header.innerHTML = nextHeader.innerHTML;
          // The swapped-in intro needs its own overflow check (see collection-intro-clamp.js).
          window.initCollectionIntroClamp?.();
        }

        document.title = markup.title;

        if (focusHref) {
          // Replacing the filter column and results drops focus to <body>
          // when the clicked chip was inside them (Codex review finding):
          // find the same destination in the freshly swapped markup and
          // focus that, or the results heading when the chip itself no
          // longer renders there (e.g. it became the active/current one).
          const candidates = document.querySelectorAll(
            '.collection-grid__filters a[href], .collection-grid__reset, .collection-header__chips a[href]'
          );
          let match = null;
          for (const candidate of candidates) {
            if (candidate.href === focusHref) {
              match = candidate;
              break;
            }
          }

          if (match) match.focus();

          // The match above can exist but sit inside a drawer that was just
          // closed (visibility: hidden), which silently fails to focus and
          // leaves the browser to drop focus to <body> instead (found while
          // verifying this fix): fall back to the results heading whenever
          // the chip itself didn't end up focused, not only when it was
          // missing outright.
          if (!match || document.activeElement !== match) {
            const heading =
              document.querySelector('.collection-header__title') ||
              results().querySelector('.collection-grid__count-total');
            if (heading) {
              if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
              heading.focus();
            }
          }
        }
      }

      if (push) history.pushState({}, '', url);
      else history.replaceState({}, '', url);
    } catch (error) {
      console.error('Kon de resultaten niet laden', error);
      window.location.assign(url);
    } finally {
      layout.removeAttribute('aria-busy');
    }
  };

  // Sold-out products last (tasks/specs/uitverkocht-achteraan). The server still renders the mixed
  // grid, so without JavaScript and for crawlers nothing changes. When the results carry the
  // [data-sold-out] template, the grid is replaced by the first in-stock page, "Toon meer" walks
  // those, and only the customer's own click adds the sold-out ones under their heading. The
  // address bar keeps the URL the customer chose, except a later page (history, a shared link):
  // that page mixed both groups, so the split starts at the top and the URL loses its page.
  let split = null;
  let splitting = null;

  const fetchSection = async (url, sectionId) => {
    const response = await fetch(sectionUrl(url, sectionId));
    if (!response.ok) throw new Error(`${response.status}`);

    // An error page can still answer 200: only a response that holds the results grid may touch
    // the page, so the current grid, buttons and state stay as they are and the retry path applies.
    const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!markup.querySelector('.collection-grid__results [data-results-products]')) {
      throw new Error('Geen resultaten in de sectie');
    }
    return markup;
  };

  const itemKey = (item) => item.querySelector('a[href]')?.pathname;

  // A product can match both availability values (one size left, another sold out), and a search
  // repeats its pages and articles on every availability page, so anything already shown is skipped.
  const appendItems = (markup, productList) => {
    const shown = new Set(
      [...results().querySelectorAll('[data-results-products] > li, [data-results-other-list] > li')].map(itemKey)
    );
    const fresh = (item) => {
      const key = itemKey(item);
      if (key && shown.has(key)) return false;
      shown.add(key);
      return true;
    };

    const products = [...markup.querySelectorAll('[data-results-products] > li')].filter(fresh);
    productList.append(...products);

    const otherList = results().querySelector('[data-results-other-list]');
    const others = otherList ? [...markup.querySelectorAll('[data-results-other-list] > li')].filter(fresh) : [];
    otherList?.append(...others);

    return { products, others };
  };

  const paintSplit = (markup) => {
    let pagination = results().querySelector('[data-split-pagination]');
    if (!pagination) {
      pagination = split.template.content.querySelector('[data-split-pagination]').cloneNode(true);
      const current = results().querySelector('.collection-grid__pagination');
      if (current) current.replaceWith(pagination);
      else results().append(pagination);

      // The sold-out results start at their first page too, also as a link for a new tab.
      const soldOut = pagination.querySelector('[data-sold-out-more]');
      soldOut.href = firstPageUrl(soldOut.getAttribute('href'));
    }

    // Within a group "Toon meer" continues that group; after its last page the sold-out button
    // takes over, until the customer has revealed them.
    const next = markup.querySelector('.collection-grid__more');
    const more = pagination.querySelector('[data-split-more]');
    if (next) more.href = pageUrl(next.getAttribute('href'));
    else more.removeAttribute('href');
    more.hidden = !next;
    pagination.querySelector('[data-sold-out-more]').hidden = Boolean(next) || split.revealed;
    pagination.querySelector('.collection-grid__split-error').hidden = true;

    // Counted against every result, both groups together, like the progress the server renders.
    const seen = results().querySelectorAll('[data-results-products] > li, [data-results-other-list] > li').length;
    const total = Math.max(split.total, seen, 1);
    pagination.querySelector('[data-split-progress]').textContent = `${seen} van ${total} bekeken`;
    pagination
      .querySelector('.collection-grid__progress-bar')
      .style.setProperty('--progress', `${Math.floor((seen * 100) / total)}%`);
  };

  const startSplit = (url = window.location.href) => {
    const owner = results();
    const template = owner?.querySelector('template[data-sold-out]');
    split = template
      ? { template, sectionId: template.dataset.sectionId, total: Number(template.dataset.total), revealed: false, reveal: null }
      : null;
    splitting = null;
    if (!split) return;

    const laterPage = isLaterPage(url);
    const pending = fetchSection(firstPageUrl(template.dataset.inStockUrl), split.sectionId)
      .then((markup) => {
        if (results() !== owner) return;
        const list = owner.querySelector('[data-results-products]');
        list.replaceChildren();
        if (laterPage) {
          owner.querySelector('[data-results-other-list]')?.replaceChildren();
          history.replaceState({}, '', firstPageUrl(window.location.href));
        }
        appendItems(markup, list);
        paintSplit(markup);
      })
      .catch((error) => {
        // The mixed grid the server rendered simply stays: every product is still there.
        console.error('Kon de leverbare producten niet laden', error);
        if (results() === owner) split = null;
      })
      .finally(() => {
        if (splitting === pending) splitting = null;
      });
    splitting = pending;
  };

  const loadSplit = async (link, { reveal = false } = {}) => {
    const owner = results();
    const pagination = link.closest('[data-split-pagination]');
    layout.setAttribute('aria-busy', 'true');

    try {
      let markup = await fetchSection(link.href, split.sectionId);
      if (results() !== owner) return;

      let list = [...owner.querySelectorAll('[data-results-products]')].pop();
      let target = null;
      if (reveal) {
        // Same grid, its own list, under the "Uitverkocht" heading; on search before the pages
        // and articles, which stay below every product as before.
        target = split.template.content.querySelector('.collection-grid__sold-out-title').cloneNode(true);
        const soldOutList = list.cloneNode(false);
        (owner.querySelector('[data-results-other]') || pagination).before(target, soldOutList);
        list = soldOutList;
        split.revealed = true;
        // Kept on split, not local: a retry through "Toon meer" after a failed fetch still has to
        // clean up an empty group.
        split.reveal = { title: target, list: soldOutList };
      }

      let { products, others } = appendItems(markup, list);
      paintSplit(markup);

      // A page whose products were all shown already (on search: still orderable in another size)
      // adds nothing, so the next page follows on its own instead of leaving an empty click.
      const more = pagination.querySelector('[data-split-more]');
      while (!products.length && !more.hidden) {
        markup = await fetchSection(more.href, split.sectionId);
        if (results() !== owner) return;
        const page = appendItems(markup, list);
        products = page.products;
        others = others.concat(page.others);
        paintSplit(markup);
      }

      // Search without a products-only count: every product the out-of-stock value listed was still
      // orderable, so no empty "Uitverkocht" group stays behind, only a short note.
      if (split.reveal && !split.reveal.list.children.length) {
        split.reveal.title.remove();
        split.reveal.list.remove();
        target = pagination.querySelector('[data-sold-out-none]');
        target.hidden = false;
      }

      // Keyboard users continue where the new items start instead of on <body> or a hidden button.
      // A page can add no product at all (only pages and articles, or only ones already shown) while
      // both buttons disappear: then the first new page or article, or else the progress line.
      target ||=
        products[0]?.querySelector('a[href]') ||
        others[0]?.querySelector('a[href]') ||
        pagination.querySelector('.collection-grid__more:not([hidden])') ||
        pagination.querySelector('[data-split-progress]');
      target?.focus({ preventScroll: true });
      split.reveal = null;
    } catch (error) {
      // Item "knop tonen lukt niet": a message, and the same button stays to try again.
      console.error('Kon de producten niet laden', error);
      pagination.querySelector('.collection-grid__split-error').hidden = false;
      // The clicked button may be hidden by now (a reveal that fell through to the next page):
      // focus the control that retries instead of leaving it on <body>.
      pagination.querySelector('.collection-grid__more:not([hidden])')?.focus({ preventScroll: true });
    } finally {
      layout.removeAttribute('aria-busy');
    }
  };

  layout.addEventListener('click', (event) => {
    const splitLink = split && event.target.closest('[data-split-more], [data-sold-out-more]');
    if (splitLink) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      if (splitting || layout.hasAttribute('aria-busy')) return;
      loadSplit(splitLink, { reveal: splitLink.hasAttribute('data-sold-out-more') });
      return;
    }

    if (event.target.closest('.collection-grid__toggle')) {
      setDrawer(!filters()?.hasAttribute('data-open'));
      return;
    }

    if (event.target.closest('.collection-grid__close') || event.target.closest('.collection-grid__backdrop')) {
      setDrawer(false);
      return;
    }

    const more = event.target.closest('.collection-grid__more');
    if (more) {
      event.preventDefault();
      // The server's mixed "Toon meer" is about to be replaced by the in-stock one.
      if (splitting) return;
      swap(more.href, { append: true, push: false });
      return;
    }

    const link = event.target.closest(
      '.tag-filter__row, .tag-filter__chip, .collection-grid__reset'
    );
    if (link?.href) {
      // A modified click (Cmd/Ctrl/Shift/Alt) or a non-primary button is the
      // browser's own way to open a link in a new tab/window; hijacking that
      // into an in-page swap breaks it (Codex review finding).
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      swap(link.href, {
        focusHref: link.href,
        insideDrawer: !!link.closest('.collection-grid__filters[data-open]'),
      });
    }
  });

  layout.addEventListener('input', (event) => {
    if (event.target.closest('.collection-grid__price')) paintPrice();
  });

  let pending;

  layout.addEventListener('change', (event) => {
    const form = event.target.closest('.collection-grid__price, .collection-grid__sort');
    if (!form) return;

    clearTimeout(pending);
    pending = setTimeout(() => {
      swap(formUrl(form.action, new FormData(form)));
    }, 300);
  });

  layout.addEventListener('pointerdown', (event) => {
    const slider = event.target.closest('.collection-grid__slider');
    if (!slider) return;

    const minInput = slider.querySelector('[data-role="min"]');
    const maxInput = slider.querySelector('[data-role="max"]');
    const bounds = slider.getBoundingClientRect();
    const position = ((event.clientX - bounds.left) / bounds.width) * Number(maxInput.max);
    const nearMin = Math.abs(position - Number(minInput.value)) <= Math.abs(position - Number(maxInput.value));

    minInput.style.zIndex = nearMin ? '3' : '2';
    maxInput.style.zIndex = nearMin ? '2' : '3';
  });

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('.collection-header__chips a');
    if (!chip?.href) return;

    // Same modifier/non-primary-click guard as the filter chips above
    // (Codex review finding).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    swap(chip.href, { focusHref: chip.href });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && filters()?.hasAttribute('data-open')) setDrawer(false);
  });

  window.addEventListener('popstate', () => swap(window.location.href, { push: false }));

  // Crossing the drawer breakpoint switches the panel to its closed position; without this the
  // browser animates that jump and the drawer appears to slide out on its own.
  let resizeTimer;

  window.addEventListener('resize', () => {
    document.documentElement.dataset.resizing = '';
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => delete document.documentElement.dataset.resizing, 200);
  });

  paintPrice();
  startSplit();
}
